import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faIndustry, faClock, faPoundSign
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Tariff-Impact-modelling.png";
import vis from "../../assets/images/use-cases/Tariff-Impact-modelling-dashboard.png";

const TariffImpactModelling = () => {
    return (
        <>
            <SEO
                title="Tariff Impact Modelling: AI Digital Twin for Supply Chain | Nebulyx AI"
                description="Model tariff scenarios across multi-country supply chains using AI Digital Twins. Simulate 20% tariff increases, market shifts, and supplier changes in real-time."
                keywords="AI digital twin, tariff modelling, supply chain simulation, data object graph, scenario analysis"
                canonicalUrl="https://nebulyx.ai/use-cases/tariff-impact-modelling"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faIndustry}/>
                                            &nbsp;
                                            Manufacturing
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;Impact of new tariffs, across supply chains?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Simulate tariff scenarios across multi-country supply chains to optimise costs, pricing, and market strategy in real-time.                                    </p>
                                </div>
                            </Col>
                        </Row>


                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">5 Markets</span>
                                <span className="stat-label">Modelled End-to-End</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faPoundSign} className="stat-icon"/>
                                <span className="stat-value">£2.4M</span>
                                <span className="stat-label">Cost Optimisation</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">Time Saved</span>
                                <span className="stat-label">Modelled in days</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Tariff changes create pricing uncertainty</li>
                                            <li>Multi-country supply chains are complex</li>
                                            <li>Spreadsheet models can't handle interdependencies</li>
                                            <li>Weeks to analyse a single scenario</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                    <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Built Data Object Graph of supply chain</li>
                                            <li>Connected manufacturing, orders, and tariffs</li>
                                            <li>AI-powered scenario engine</li>
                                            <li>Real-time cost propagation modelling</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Scenario analysis in minutes not weeks</li>
                                            <li>£2.4M cost optimisation identified</li>
                                            <li>Proactive tariff response strategy</li>
                                            <li>Confident market entry decisions</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Ingests manufacturer, tariff and order data to build a Data Object Graph that simulates cost
                            impacts and outputs optimised pricing strategies.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Sources</strong>
                                    <span>Manufacturing costs, tariff rates, ecommerce orders across US, UK, Germany</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Graph</strong>
                                    <span>Data Object Graph maps nodes, edges, and dependencies across supply chain</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Run Scenarios</strong>
                                    <span>What-if engine propagates tariff changes through cost model</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Optimise Output</strong>
                                    <span>Recommended pricing, sourcing, and market strategies per region</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from the manufactures and US, UK and German sales channels delivery data to model
                                            the supply chain costs and identify the real-world P&L impact. Simulate what-if scenarios
                                            recover the revenue by modelling product upsell in low tariff regions.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Tariff Modelling Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Displays current supply chain impacts, allows adjustment of different splits
                                            between sales channels and supply route scenarios to margin and revenue impacts.
                                        We can also do sophisticated graph analysis on the supply chain routes to calculate costs</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>China Manufacturer data</li>
                                            <li>Vietnam Manufacturer data</li>
                                            <li>Manufacturer Tariffs</li>
                                            <li>Ecommerce Orders (US/UK/DE)</li>
                                            <li>Order Management System</li>
                                            <li>Supply Chain Metrics</li>
                                            <li>Cost Model</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Supply Chain Graph analysis</li>
                                            <li>Tariff impact calculation</li>
                                            <li>Margin sensitivity analysis</li>
                                            <li>Demand elasticity modelling</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Tariff increase +20%</li>
                                            <li>US orders -30%, EU +15%</li>
                                            <li>China market entry</li>
                                            <li>Vietnam supplier shift</li>
                                            <li>Currency fluctuation ±10%</li>
                                            <li>Dual sourcing strategy</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the full supply chain as a <strong>Data Object Graph</strong>, we
                                        can find scenarios like shifting 40% of production to Vietnam and adjusting US pricing by 8%
                                        <strong> can fully offset a 20% tariff increase</strong> while maintaining margin
                                        targets.
                                        Graph traversal revealed hidden cost dependencies invisible to spreadsheet
                                        analysis.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">40/60 Supplier Split</span>
                                        <span className="discovery-note">Tariff-resilient configuration</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Facing Tariff Uncertainty?</h2>
                            <p>
                                If your supply chain spans multiple countries and tariff changes keep you guessing,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default TariffImpactModelling;